import type { TradingSignal } from '../../types'
import { formatPrice } from '../../utils/marketPrice'
import { pricePathPositions } from '../../utils/signalPnl'

// Labels stay inside the card: one near an edge grows inward from its marker
// instead of centring on it, so a long "Target $83,700.00" never overhangs a
// phone-width bar.
const anchor = (pct: number) =>
  pct < 25 ? 'translate-x-0' : pct > 75 ? '-translate-x-full' : '-translate-x-1/2'

const Label = ({ at, children, className, testId }: { at: number; children: string; className: string; testId: string }) => (
  <span
    className={`absolute whitespace-nowrap font-mono text-[11px] tabular-nums ${anchor(at)} ${className}`}
    style={{ left: `${Math.min(Math.max(at, 0), 100)}%` }}
    data-testid={testId}
  >
    {children}
  </span>
)

const Tick = ({ at, className }: { at: number; className: string }) => (
  <span aria-hidden="true" className={`absolute top-1/2 h-3.5 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded ${className}`} style={{ left: `${at}%` }} />
)

/**
 * Stop, entry, current price and target on one line, spaced by price.
 * Stop, target and current are labelled above the bar, entry below. Prices
 * grow left to right, so a SHORT reads target · entry · stop. A closed signal's
 * marker is its exit price, so it reads "Exit" rather than "Now".
 */
const PricePathBar = ({ signal, current }: { signal: TradingSignal; current: number | null }) => {
  const p = pricePathPositions(signal, current)
  const lossFrom = Math.min(p.stop, p.entry)
  const gainFrom = Math.min(p.entry, p.target)
  const d = (v: number) => `$${formatPrice(v)}`
  const markLabel = signal.status === 'closed' ? 'Exit' : 'Now'

  return (
    <div
      className="relative select-none"
      role="img"
      aria-label={`Stop ${d(signal.stop_loss)}, entry ${d(signal.entry_price)}, target ${d(signal.take_profit)}${
        current != null ? `, ${markLabel.toLowerCase()} ${d(current)}` : ''
      }`}
      data-testid="price-path"
    >
      {/* labels above: stop and target */}
      <div className="relative h-4">
        <Label at={p.stop} className="text-red-300" testId="path-stop">{`Stop ${d(signal.stop_loss)}`}</Label>
        <Label at={p.target} className="text-green-300" testId="path-target">{`Target ${d(signal.take_profit)}`}</Label>
      </div>
      {/* label above: current (its own row, so it never collides with stop/target) */}
      <div className="relative h-4">
        {p.current != null && current != null && (
          <Label at={p.current} className="font-semibold text-accent" testId="path-current">{`${markLabel} ${d(current)}`}</Label>
        )}
      </div>
      <div className="relative my-1 h-2 rounded-full bg-white/10">
        <span
          aria-hidden="true"
          className="absolute inset-y-0 rounded-l-full bg-red-500/40"
          style={{ left: `${lossFrom}%`, width: `${Math.abs(p.entry - p.stop)}%` }}
        />
        <span
          aria-hidden="true"
          className="absolute inset-y-0 rounded-r-full bg-green-500/40"
          style={{ left: `${gainFrom}%`, width: `${Math.abs(p.target - p.entry)}%` }}
        />
        <Tick at={p.stop} className="bg-red-400" />
        <Tick at={p.entry} className="bg-gray-200" />
        <Tick at={p.target} className="bg-green-400" />
        {p.current != null && (
          <span
            aria-hidden="true"
            className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-terminal-surface bg-accent shadow"
            style={{ left: `${p.current}%` }}
            data-testid="path-current-marker"
          />
        )}
      </div>
      {/* label below: entry */}
      <div className="relative h-4">
        <Label at={p.entry} className="text-gray-300" testId="path-entry">{`Entry ${d(signal.entry_price)}`}</Label>
      </div>
    </div>
  )
}

export default PricePathBar
